import React, { useEffect, useState } from 'react';
import {
  Flex,
  Box,
  Text,
  Button,
  Icon,
  Link,
  Accordion,
  useToast,
  FormControl,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { Link as WouterLink, useLocation } from 'wouter';
import { BsArrowLeft } from 'react-icons/bs';
import { FormProvider, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClosedCaptioning } from '@fortawesome/free-regular-svg-icons';
import linkedinLogo from '../images/linkedIn.svg';
import linkedinLogoGrey from '../images/linkedInGrey.svg';
import resume from '../images/resume.svg';
import { useDispatch, useTrackedState } from '../config/store';
import CandidateAccordion from '../components/interview/CandidateAccordion';
import JobOpeningAccordion from '../components/interview/JobOpeningAccordion';
import InterviewAccordion from '../components/interview/InterviewAccordion';
import RecommendationAccordion from '../components/interview/RecommendationAccordion';
import InterviewsError from '../components/interviews/InterviewsError';
import CandidateResumePDF from '../components/interview/CandidateResumePDF';
import { buildChecklistFormKey, checklistBelongsToJobOpening } from '../helpers/checklist';

function Interview({ params }) {
  const [_location, setLocation] = useLocation();
  const methods = useForm();
  const dispatch = useDispatch();
  const state = useTrackedState();
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const [haveSubmitted, setHaveSubmitted] = useState(false);
  const { fetchCandidateResume, openCandidateResume, hasResumePDF, isFetchingPDF } = CandidateResumePDF(
    state.interview,
  );

  useEffect(() => {
    if (
      state.user &&
      state.interview &&
      (!state.resumePDF?.url || state.resumePDF?.id !== state.interview.interviewData.Candidate_Name.id)
    ) {
      fetchCandidateResume();
    }
  }, [state.interview, state.user]);

  useEffect(() => {
    if (state.invalidInterview && !state.pending) {
      setLocation('/interviews/404');
    }
  }, [state.invalidInterview, state.pending]);

  // Get Metadata and single Interview, if user loggedin and loaded
  useEffect(() => {
    if (state && state.user) {
      if (!state.metadata) {
        dispatch({ type: 'GET_METADATA', token: state.user.token });
      }

      if (!state.interview || state.interview !== params.id) {
        dispatch({
          type: 'GET_INTERVIEW_AND_STORE',
          token: state.user.token,
          interviewId: params.id,
        });
      }
    }
  }, [state.user]);

  // Handle submitting state, either redirect to Interviews list or show error
  useEffect(() => {
    // While submitting, set a 10s timeout to reset SUBMIT button
    if (submitting) {
      const submittingTimeout = setTimeout(() => {
        setSubmitting(false);
      }, 10000);
      return () => clearTimeout(submittingTimeout);
    }
    return undefined;
  }, [submitting]);

  useEffect(() => {
    if (state.interviewSubmitStatus) {
      setSubmitting(false);

      // If interviewSubmitStatus success, redirect, otherwise show the error
      if (state.interviewSubmitStatus === 'record updated') {
        const interviewId = state.interview.interviewData.id;
        dispatch({ type: 'TEMPORAL_LOCK_INTERVIEW', interviewId });
        setLocation('/interviews');
      } else if (state.interviewSubmitStatus === 'Fail Validation') {
        let errorMessageText = state.interviewSubmitValidation;
        // Remove trailing period if present
        if (errorMessageText.endsWith('.')) {
          errorMessageText = errorMessageText.slice(0, -1);
        }
        const errorMessages = errorMessageText.split('. ');
        errorMessages.forEach(errorMessage => {
          if (errorMessage) {
            toast({
              position: 'bottom-right',
              isClosable: true,
              title: 'Validation Error',
              description: `${errorMessage}.`,
              status: 'warning',
              duration: null,
              variant: 'left-accent',
            });
          }
        });
      } else {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: 'Unexpected Error',
          description: `Try again later or contact us at screeners-feedback@nextonlabs.com explaining what happened so that we can work on a fix!`,
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
      }
    }
  }, [state.interviewSubmitStatus, state.interviewSubmitValidation]);

  // Reset / clear Interview data form
  useEffect(() => {
    if (!state?.interview?.interviewData) return;

    // Only build it from the checklist that actually belongs to this interview's job
    // opening. When it doesn't match we reset with an empty checklist.
    const currentJobOpeningId = state?.interview?.interviewData?.Posting_Title?.id;
    const sanitizeChecklistData = {};

    if (
      checklistBelongsToJobOpening(state?.checklistItems, currentJobOpeningId) &&
      state?.checklistItems?.checks &&
      state?.interview?.interviewData?.Checklist_Items
    ) {
      const resumeChecklist = state.checklistItems.checks.filter(item => item.text !== '');
      const requiredChecklist = resumeChecklist.filter(item => item.required);
      const notRequiredChecklist = resumeChecklist.filter(item => !item.required);
      const totalChecklist = [...requiredChecklist, ...notRequiredChecklist];
      totalChecklist.forEach((item, idx) => {
        const formKey = buildChecklistFormKey(item, idx);
        const checklistOnDB = state?.interview?.interviewData?.Checklist_Items?.find(check =>
          check.Item_Id ? `${check.Item_Id}` === `${item.id}` : check.Checklist_Item === item.text,
        );
        sanitizeChecklistData[formKey] = {
          Item_Id: item.id,
          Checklist_Item: item.text,
          Screeners_Decision: checklistOnDB?.Screeners_Decision,
          Screeners_Comment: checklistOnDB?.Screeners_Comment,
        };
      });
    }

    methods.reset({ ...state.interview.interviewData, Checklist_Items: sanitizeChecklistData });
  }, [state.interview?.interviewData?.id, state?.checklistItems?.JobOpeningId]);

  const handleRetryLoad = () => {
    dispatch({ type: 'CLEAN_ERROR' });
    dispatch({ type: 'GET_METADATA', token: state.user?.token });
    dispatch({ type: 'GET_INTERVIEW_AND_STORE', token: state.user?.token, interviewId: params.id });
  };

  const dispatchInterviewSubmit = submitInterview => {
    dispatch({ type: 'UPDATE_TOKEN_AND_POST_INTERVIEW', submitInterview });
    setSubmitting(true);
    setHaveSubmitted(true);
  };

  // Curate and submit data
  const onSubmit = data => {
    const curatedData = { ...data };

    // The form keeps Checklist_Items as an object keyed by item id; the backend expects an array.
    if (
      curatedData.Checklist_Items &&
      typeof curatedData.Checklist_Items === 'object' &&
      !Array.isArray(curatedData.Checklist_Items)
    ) {
      // Backstop against cross-interview contamination: only submit items that belong to the
      // checklist of this interview's job opening. When the loaded checklist belongs to a
      // different job opening (it is still being fetched, or its request failed) the valid set is
      // empty, so nothing is submitted — this used to fall back to "no filtering", which submitted
      // the previous job opening's items against this candidate.
      const currentJobOpeningId = state?.interview?.interviewData?.Posting_Title?.id;
      const checklistMatchesJob = checklistBelongsToJobOpening(state?.checklistItems, currentJobOpeningId);
      const validFormKeys = new Set(
        checklistMatchesJob
          ? (state?.checklistItems?.checks || [])
              .filter(item => item.text !== '')
              .map((item, idx) => buildChecklistFormKey(item, idx))
          : [],
      );

      curatedData.Checklist_Items = Object.entries(curatedData.Checklist_Items)
        .filter(([formKey]) => validFormKeys.has(formKey))
        .map(([, value]) => value);
    }

    const expectedSalary = curatedData.Expected_monthly_salary_USD;
    const currentSalary = curatedData.Current_monthly_salary_USD;
    const declinedToShareSalary = curatedData.Candidate_declined_to_share_current_salary === true;
    const validationBlockedReason =
      !curatedData.Interview_Blocked_Reason || curatedData.Interview_Blocked_Reason === '-None-';

    // Both states are mutually exclusive: either a valid current salary or the checkbox checked.
    if (declinedToShareSalary) {
      curatedData.Current_monthly_salary_USD = '';
    }

    if (!declinedToShareSalary && (currentSalary < 0 || !Number(currentSalary)) && validationBlockedReason) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Current Salary Error',
        description: `Please enter a value greater than 0. If the candidate refused to share, check 'Candidate declined to share' to proceed.`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      return;
    }
    if ((expectedSalary < 0 || !Number(expectedSalary)) && validationBlockedReason) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Expected Salary Error',
        description: `Invalid value. The number must be greater than 0.`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      return;
    }

    if (curatedData.Interview_URL) {
      const urlValue = curatedData.Interview_URL.trim();
      const hasSpaces = /\s/.test(urlValue);
      let isValidUrl = false;
      try {
        const _ = new URL(urlValue);
        isValidUrl = true;
      } catch {
        isValidUrl = false;
      }
      if (hasSpaces || !isValidUrl) {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: 'Video Recording URL Error',
          description: 'Please enter a valid URL (without spaces).',
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
        return;
      }
    }

    const validationShadowingSession = curatedData.Shadow_Session && !curatedData.Trainees_Name;
    const validationNonTech =
      !curatedData.Non_Tech_Position &&
      (!curatedData.Seniority_Level_By_Tech || !curatedData.Seniority_Level_By_Tech[0]);
    const validationEmptySeniorityLevel = curatedData.Seniority_Level_By_Tech?.find(
      item => item.Tech !== '' && item.Experience === '',
    );
    const validationEmptyItemSeniorityLevelByTech = curatedData.Seniority_Level_By_Tech?.find(
      item => (item.Tech === '' && item.Experience === '') || item.Tech === '',
    );

    const errorValidationMessage = (title, description) => {
      const toastMessage = () => {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: `${title} Error`,
          description: `Please complete the ${description}`,
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
      };
      return { toastMessage };
    };

    const shadowingSessionToast = errorValidationMessage('Shadowing Session', `Shadow name`);
    const nonTechToast = errorValidationMessage(
      'Strongest Technology Stack',
      `Strongest Technology Stack or choose the non-tech position checkbox`,
    );
    const candidateGoalsToast = errorValidationMessage(`Candidate's goals`, `Candidate's goals`);
    const englishLevelToast = errorValidationMessage('English Level', `English Level`);
    const softSkillsToast = errorValidationMessage('Soft Skills Score', `Soft Skills Score`);
    const expectedSalaryToast = errorValidationMessage('Monthly Salary', `Expected Monthly Salary`);
    const nextonStatusToast = errorValidationMessage(`Candidate's Status`, `Candidate's Status`);
    const generalScoreToast = errorValidationMessage(`Job Opening Specific Score`, `Job Opening Specific Score`);
    const generalScore2Toast = errorValidationMessage(`General Score`, `General Score`);
    const mainRoleToast = errorValidationMessage(`Position and Seniority recommendation`, `Position recommendation`);
    const recommendedSeniorityToast = errorValidationMessage(
      `Position and Seniority recommendation`,
      `Seniority recommendation`,
    );
    const emptySeniorityLevelToast = errorValidationMessage(
      `Strongest Technology Stack`,
      `Experience level for all stacks`,
    );
    const emptySeniorityLevelByTechToast = errorValidationMessage(
      `Strongest Technology Stack`,
      `the rows with empty "Programming Language / Framework" or remove them`,
    );

    if (validationBlockedReason && validationShadowingSession) {
      shadowingSessionToast.toastMessage();
      return;
    }
    if (validationBlockedReason && validationNonTech && curatedData.Interview_Type !== 'Coaching Session') {
      nonTechToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Candidate_goals) {
      candidateGoalsToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.English_level && curatedData.Interview_Type !== 'Coaching Session') {
      englishLevelToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Soft_skills) {
      softSkillsToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Expected_monthly_salary_USD &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      expectedSalaryToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Nexton_Status && curatedData.Interview_Type !== 'Coaching Session') {
      nextonStatusToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.General_Score && curatedData.Interview_Type !== 'Coaching Session') {
      generalScoreToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.General_Score2 && curatedData.Interview_Type !== 'Coaching Session') {
      generalScore2Toast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Main_role?.length &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      mainRoleToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Recommended_Seniority?.length &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      recommendedSeniorityToast.toastMessage();
      return;
    }
    if (validationBlockedReason && validationEmptySeniorityLevel && curatedData.Interview_Type !== 'Coaching Session') {
      emptySeniorityLevelToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      validationEmptyItemSeniorityLevelByTech &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      emptySeniorityLevelByTechToast.toastMessage();
      return;
    }

    Object.keys(curatedData).forEach(
      k => ((!curatedData[k] && curatedData[k] !== undefined) || curatedData[k] === '') && delete curatedData[k],
    );

    dispatchInterviewSubmit(curatedData);
  };

  // Alert user. Custom value/text is ignored, hence just ''
  const alertUser = event => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = '';
  };
  const handleTabClosing = () => {}; // No action on unload for now

  // Listen for window when trying to close or reload this page, cleanup afterwards
  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    window.addEventListener('unload', handleTabClosing);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
      window.removeEventListener('unload', handleTabClosing);
    };
  });

  // Show a retryable error instead of an infinite spinner. Each blocking failure carries its own
  // flag: `state.error` is also set by side requests that leave the report perfectly usable, and
  // nothing scoped it to this screen, so a leftover one used to render here while the interview
  // was still loading.
  if (state.metadataError || state.interviewError) {
    return <InterviewsError title='There was an error loading the interview!' onRetry={handleRetryLoad} />;
  }

  if (!state.interview || !state.metadata || !state.experienceMetadata) {
    return (
      <Flex justifyContent='center' alignItems='center' minH='60vh' w='100%'>
        <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='xl' />
      </Flex>
    );
  }

  return (
    <Box maxW='910px' minW='910px' px='20px' mt='24px' pb='32px'>
      <Flex justifyContent='space-between' alignItems='center' mb='20px'>
        <Flex alignItems='center'>
          <WouterLink href='/interviews'>
            <Button size='sm' backgroundColor='white' color='#337bff' px='5px'>
              <Icon as={BsArrowLeft} boxSize={4} />
            </Button>
          </WouterLink>
          <Text
            fontSize='24px'
            fontWeight='400'
            color='gray.900'
            fontFamily='K2D, sans-serif'
            letterSpacing='-1px'
            ml='31px'
            paddingRight='10px'>
            {state.interview.interviewData?.Candidate_Name?.name}
          </Text>
          {state?.interview?.interviewData.Interview_Type === 'Catch up' && (
            <Flex alignSelf='center'>
              <Tooltip
                fontSize='sm'
                fontWeight='400'
                color='grey.900'
                bg='white'
                border='1px solid #D0D0D0'
                boxShadow='0px 41px 60px 11px rgba(161, 161, 161, 0.160047)'
                borderRadius='16px'
                p='24px'
                maxWidth='400px'
                aria-label='A tooltip'
                placement='bottom-start'
                label='Catch up Call'>
                <Icon boxSize={7}>
                  <FontAwesomeIcon icon={faClosedCaptioning} size='2xl' style={{ color: '#ff6d00' }} />
                </Icon>
              </Tooltip>
            </Flex>
          )}
        </Flex>
        <Flex gap={6}>
          <Flex flexDirection='column' alignItems='center' justifyContent='center'>
            {isFetchingPDF ? (
              <Spinner thickness='2px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='lg' />
            ) : (
              hasResumePDF && (
                <Button
                  variant='link'
                  colorScheme='blue'
                  onClick={() => {
                    openCandidateResume();
                  }}
                  marginBottom='15px'
                  marginTop='15px'>
                  <Flex alignItems='center'>
                    <img src={resume} className='w-[18px] h-[18px]' alt='Resume logo' />
                    <Text fontSize='16px' fontWeight='400' ml='8px'>
                      Resume
                    </Text>
                  </Flex>
                </Button>
              )
            )}
          </Flex>
          {state.interview.candidateData?.Snapshot_LinkedIn_Profile && (
            <Flex flexDirection='column' alignItems='center'>
              <Button variant='link' colorScheme={hasResumePDF ? 'gray' : 'blue'} marginBottom='15px' marginTop='15px'>
                <Link href={state.interview.candidateData?.Snapshot_LinkedIn_Profile} target='_blank'>
                  <Flex alignItems='center'>
                    <img
                      src={hasResumePDF ? linkedinLogoGrey : linkedinLogo}
                      className='w-[18px] h-[18px]'
                      alt='Linkedin logo'
                    />
                    <Text fontSize='16px' fontWeight='400' ml='8px'>
                      LinkedIn Profile
                    </Text>
                  </Flex>
                </Link>
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormControl>
            <Accordion defaultIndex={[0]} allowMultiple>
              <CandidateAccordion interview={state.interview} />
              <JobOpeningAccordion interview={state.interview} />
              <InterviewAccordion
                interview={state.interview}
                metadata={state.metadata}
                experienceMetadata={state.experienceMetadata}
              />
              <RecommendationAccordion interview={state.interview} metadata={state.metadata} />
            </Accordion>
            <Flex justifyContent='center'>
              {/* This disabled submit button prevents implicit form submission */}
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <button type='submit' disabled style={{ display: 'none' }} />

              <Button
                type='submit'
                isLoading={submitting}
                loadingText='Submitting'
                width='140px'
                height='44px'
                color='white'
                backgroundColor='green.500'
                _hover={{
                  bg: 'green.700',
                }}
                _active={{
                  bg: 'green.800',
                }}
                borderRadius='10px'
                fontSize='16px'>
                Submit
              </Button>
            </Flex>
          </FormControl>
        </form>
      </FormProvider>
      {state.error && haveSubmitted && <InterviewsError />}
    </Box>
  );
}

export default Interview;
